import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, FormControl, InputLabel, Select, MenuItem, Button,
  Dialog, DialogContent, CircularProgress, Snackbar, Alert
} from '@mui/material';
import CorrectionModal from './CorrectionModal';
import './ProfilAnonymise.css';
import { MODEL_LABELS } from '../../../commun/constants/options';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const ProfilAnonymise = ({ talent, apiBase = 'http://localhost:3001' }) => {
  const [modele, setModele] = useState('option1');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [correctionData, setCorrectionData] = useState(null);
  const [generatedCVs, setGeneratedCVs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (!talent?._id) return;

    fetch(`${apiBase}/api/cv-generated/list/${talent._id}`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') setGeneratedCVs(res.data);
        else setGeneratedCVs([]);
      })
      .catch(() => setGeneratedCVs([]));

    fetch(`${apiBase}/api/cv-corrections/${talent._id}`)
      .then(res => res.ok ? res.json() : null)
      .then(res => {
        if (res?.data) setCorrectionData(res.data);
        else setCorrectionData(null);
      })
      .catch(() => setCorrectionData(null));
  }, [talent, apiBase]);

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(s => ({ ...s, open: false }));
  };

  const handleOpenHistoricCorrection = () => {
  if (correctionData) {
    setExtractedData(correctionData);
    setModalOpen(true);
  } else {
    showMessage("Aucune donnée à afficher.", "warning");
  }
};


  const handleCVGenerated = (cvInfo) => {
    if (!cvInfo) return;
    setGeneratedCVs(prev => [cvInfo, ...prev]);
    showMessage('CV anonymisé ajouté à l\'historique', 'success');
  };

  const handleGenerate = async () => {
    if (!modele) {
      showMessage("Veuillez sélectionner un modèle de CV.", 'warning');
      return;
    }
    if (!talent?.cvUpload) {
      showMessage("Le talent ne possède pas de CV à anonymiser.", 'warning');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiBase}/api/recruteur/uploads/${talent.cvUpload}`);
      if (!response.ok) throw new Error("Impossible de récupérer le CV.");
      const cvBlob = await response.blob();

      const formData = new FormData();
      formData.append('file', cvBlob, talent.cvUpload);
      formData.append('choice', modele);

      const externalResponse = await fetch('https://www.cooptaleo.com/corrections/', {
        method: 'POST',
        body: formData,
      });

      if (!externalResponse.ok) throw new Error("Erreur lors de l'appel à l'API externe.");

      const jsonResult = await externalResponse.json();
      if (jsonResult.status === 'success') {
        setExtractedData(jsonResult.data);
        setModalOpen(true);
      } else {
        showMessage("Aucune donnée extraite.", 'error');
      }

    } catch (err) {
      console.error("Erreur :", err);
      showMessage("Une erreur est survenue. Vérifiez le serveur distant.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReopenCorrection = () => {
    if (correctionData) {
      setExtractedData(correctionData);
      setModalOpen(true);
    } else {
      showMessage("Aucune donnée à corriger.", "warning");
    }
  };

  return (
    <>
      <Paper elevation={3} className="pa-paper-box">
        <Typography variant="h6" className="pa-title">
          Génération du profil anonyme
        </Typography>
        <Typography variant="body2" className="pa-subtitle">
          Sélectionnez un modèle pour générer un document anonymisé du talent.
        </Typography>

        <div className="pa-model-select-preview">
          <FormControl fullWidth margin="normal" className="pa-form-control">
            <InputLabel id="modele-cv-label">Modèle de CV</InputLabel>
            <Select
              labelId="modele-cv-label"
              value={modele}
              onChange={(e) => setModele(e.target.value)}
              disabled={!!correctionData}
            >
              <MenuItem value="option1">Modèle 1</MenuItem>
            </Select>
          </FormControl>
          <div className="pa-model-preview-box">
            <img
              src="/modeles/Mo1.png"
              alt="Aperçu Modèle 1"
              className="pa-model-preview-img"
            />
          </div>
        </div>

        {!correctionData ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="pa-generate-btn"
            onClick={handleGenerate}
            disabled={!modele || loading}
          >
            {loading ? "Génération en cours..." : "Générer et Envoyer le CV"}
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            className="pa-generate-btn"
            onClick={handleReopenCorrection}
          >
            Corriger
          </Button>
        )}
      </Paper>

      <CorrectionModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        data={extractedData}
        model={modele}
        talentId={talent?._id}
        onCVGenerated={handleCVGenerated}
      />

     <div className="pa-cv-history-list">
  <Typography variant="h6" className="pa-history-title">Historique des CV anonymisés générés</Typography>
  {generatedCVs.map((cv, idx) => (
    <Paper key={cv._id || idx} className="pa-cv-history-item">
      <Typography variant="subtitle1" className="pa-cv-history-item-title">
        {cv.name && <b>{cv.name}</b>}
        {cv.name && cv.title && " - "}
        {cv.title && <b>{cv.title}</b>}
        {" – Modèle : "}
        <b>{MODEL_LABELS[cv.model]}</b>
      </Typography>
      <Typography variant="body2" className="pa-cv-history-item-date">
        Généré le : {new Date(cv.generatedAt || cv.date).toLocaleString()}
      </Typography>
      <Button
        variant="outlined"
        className="pa-download-btn"
        size="small"
        onClick={handleOpenHistoricCorrection}
      >
        Voir le détail
      </Button>
    </Paper>
  ))}
</div>


      <Dialog open={loading} maxWidth="xs" fullWidth>
        <DialogContent className="pa-loader-container">
          <SmartToyIcon className="pa-ai-icon" />
          <Typography variant="h6" className="pa-loader-title">
            Génération en cours...
          </Typography>
          <CircularProgress className="pa-loader" />
          <Typography variant="body2" className="pa-loader-text">
            Le profil anonymisé est en train d’être généré par l’IA, merci de patienter.
          </Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfilAnonymise;
